import React, { FC, ReactNode } from 'react'
import 'mapbox-gl/dist/mapbox-gl.css'
import { IProperty } from 'src/interfaces/IProperty'
import { useTranslation } from 'react-i18next'
import { MapView } from 'src/components/elements/modals/announcement/components'
import PropertyParameters from 'src/components/elements/property-parameters/PropertyParameters'
import SectionBoundary from 'src/components/elements/error-boundary/SectionBoundary'

interface IPropertyDetails {
  property?: IProperty
}

// A section of the listing: the design system's card with a 22px heading
const Card: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-3 p-5 md:p-6 rounded-2xl bg-surface shadow-card">
    <h2 className="m-0 text-[1.375rem] font-extrabold leading-7 text-ink">{title}</h2>
    {children}
  </section>
)

// What the listing is: its description, its parameters and where it is
const PropertyDetails: FC<IPropertyDetails> = ({ property }) => {
  if (!property) return null
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4 md:gap-6 min-w-0">
      {property.description && (
        <Card title={t('announcement.description_title')}>
          {/* 1rem text with the line breaks kept — the client compared
              description readability to dom.ria (roadmap 16.1) */}
          <p className="m-0 max-w-[40rem] text-base leading-6 text-ink whitespace-pre-line break-words">
            {property.description}
          </p>
        </Card>
      )}
      <Card title={t('announcement.property_info')}>
        <PropertyParameters parameters={property.parametersSection} />
      </Card>
      <Card title={t('announcement.location')}>
        {property.addressString && (
          // select-all: one tap selects the whole address for copying
          <p className="m-0 text-base leading-6 text-ink break-words select-all">
            {property.addressString}
          </p>
        )}
        <div className="w-full rounded-xl overflow-hidden">
          {/* a failing map must not take the listing down with it */}
          <SectionBoundary message={t('map_unavailable')}>
            <MapView
              latitude={property.location.coordinates[1]}
              longitude={property.location.coordinates[0]}
            />
          </SectionBoundary>
        </div>
      </Card>
    </div>
  )
}

export default PropertyDetails
